import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Input, orderNoShort } from '@cuito/ui';
import type { OfflineQueueStore } from '@cuito/api-client';
import type { OrderPaidConfirmedEvent, PaymentMethod, SyncOperationInput } from '@cuito/types';
import { readErrorCode, readServerDetails, readServerMessage } from '../../lib/apiError';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';
import {
  completeOrder,
  confirmPayment,
  fetchOrderDetail,
  findOrderIdByNo,
  pickLines,
  shortLines,
  updateOrderNotes,
  type OrderDetail,
  type PickResponse,
  type ShortResponse,
} from '../api/orders';
import PaymentConfirmModal, { paymentMethodLabel, VERIFY_REQUIRED_TEXT } from '../components/PaymentConfirmModal';
import PickLineRow from '../components/PickLineRow';
import { useCashierStream } from '../CashierLayout';
import { ORDER_STATUS_CLASS, ORDER_STATUS_TEXT } from '../components/OrderCard';
import { formatDataAsOf, freshnessOf, isStale, type DataFreshness } from '../data/dataAsOf';
import { offlineActions } from '../offline/offlineActions';
import { isOfflineScan } from '../offline/offlineScans';
import { browserOfflineQueue, enqueueOperation } from '../offline/queue';

/**
 * 挑拣清单 + 收款确认 + 交付门（§3.3 `/cashier/orders/:orderNo` 的上下两半区、§6.3、§6.4）。
 *
 * 整单金额与进度已经按服务端口径渲染：
 *   · 进度 `已挑拣 N/M` 的 N = `lines.filter(line => line.done).length` —— 只数**服务端说完成**的行；
 *   · 整单金额 `order-total` 直接取 `order.totalAmount`，**没有任何前端累加**（弹窗里的金额同源）。
 *
 * 两条取数路径（都要有测试）：
 *   ① 从接单面板进来：`navigate(..., { state: { orderId } })`；
 *   ② 刷新/深链：只有 `:orderNo`，用队列端点反查 `findOrderIdByNo`（契约没有按订单号查的端点）。
 *
 * ⚠️ 根锚点 `cashier-order-page` **三个分支都在场**（`routes.spec.tsx` 按它定位这条路由）：
 * 骨架页被替换后它必须留下，否则就是「冻结锚点被本任务改掉」。
 */
export function mergeLineResults(order: OrderDetail, result: PickResponse | ShortResponse): OrderDetail {
  const byLine = new Map(result.lines.map((entry) => [entry.lineId, entry]));
  const shortResponse = 'totalAmount' in result ? result : null;
  return {
    ...order,
    status: result.status,
    ...(shortResponse === null ? {} : { totalAmount: shortResponse.totalAmount }),
    lines: order.lines.map((line) => {
      const updated = byLine.get(line.id);
      if (updated === undefined) return line;
      return {
        ...line,
        pickedQty: updated.pickedQty,
        shortQty: updated.shortQty ?? line.shortQty,
        // ⚠️ `done` 一律用服务端那一份：这里没有任何 `>=` 判断
        done: updated.done,
      };
    }),
  };
}

/** 地点还没到位时的如实提示（地点一到就收掉它，见页面里的 `pinnedLocationId` effect） */
export const ORDER_NO_LOCATION_TEXT = '请先选择地点';

/** 本机没有离线队列库（无 IndexedDB）时的如实提示：**不许**假装记下了 */
export const OFFLINE_QUEUE_UNAVAILABLE_TEXT = '本机没有离线队列，离线期间的记录无法保存，请联网后再操作';

/** 离线入队后的三句提示（§6.4 规则 5 的句式：`当前离线，…将在联网后上传`） */
export const OFFLINE_ACCEPT_QUEUED_TEXT = '当前离线，接单记录将在联网后上传';
export const OFFLINE_PICK_QUEUED_TEXT = '当前离线，挑拣记录将在联网后上传';
export const OFFLINE_PAID_QUEUED_TEXT = '当前离线，确认记录将在联网后上传';
export const OFFLINE_COMPLETE_QUEUED_TEXT = '当前离线，交付记录将在联网后上传';

export interface CashierOrderPageProps {
  /**
   * 测试注入用（`undefined` = 浏览器里那个 IndexedDB 队列；`null` = 本机没有队列）。
   *
   * 与扫码页的 `camera` 同一个体例：离线队列是**环境能力**，页面必须能从外面换掉它，
   * 否则「断网时到底记下了没有」这条判据只能靠真浏览器验。
   */
  offlineStore?: OfflineQueueStore | null;
}

export default function CashierOrderPage({ offlineStore }: CashierOrderPageProps = {}) {
  const { orderNo = '' } = useParams<{ orderNo: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const stream = useCashierStream();
  const stateOrderId = (location.state as { orderId?: string } | null)?.orderId ?? null;

  /**
   * 离线队列库（Phase 9）：断网期间的写操作进它，联网后由 `/cashier/offline` 页补传。
   *
   * `useMemo` 是必需的：`browserOfflineQueue()` 每次调用都新建一个（并重开一次 IndexedDB），
   * 写成渲染期直接调用会让每个回调的依赖每次都变 —— 与扫码页的 `cameraImpl` 同一个坑。
   */
  const queueStore = useMemo<OfflineQueueStore | null>(
    () => (offlineStore === undefined ? browserOfflineQueue() : offlineStore),
    [offlineStore],
  );

  /**
   * 本机此刻是否断网（`window` 的 `online` / `offline` 事件）。
   *
   * 用状态而不是渲染期读 `navigator.onLine`：网在弹窗开着的时候断掉/恢复，
   * 「要不要勾核对」这件事必须跟着变 —— 读一次快照的话，界面会停在一个已经不成立的判断上。
   */
  const [browserOffline, setBrowserOffline] = useState<boolean>(() => navigator.onLine === false);
  useEffect(() => {
    const goOffline = (): void => setBrowserOffline(true);
    const goOnline = (): void => setBrowserOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  /**
   * 地点从**布局的流上下文**订阅，不在挂载时快照 `readCashierLocationId()`（Task 24 验收缺陷 3
   * 的第三处同源现场：前两处是扫码页与手动建单页）。
   *
   * 冷启动深链 `/cashier/orders/<orderNo>` 的顺序是「本页先挂载、`CashierLayout` 的
   * `GET /locations` 后回来」：快照写法读到的是 `null` 且**再也不重读** ⇒ 连队列反查
   * (`findOrderIdByNo`) 都不发，收银端最常用的挑拣/收款/交付页永远停在「请先选择地点」。
   * 订阅之后，布局写入上下文 ⇒ 本页自动重渲染，取详情照常。
   */
  const { locationId } = stream;

  /**
   * 本页**钉住**的地点 = 本页见到的**第一个**非空地点：挂载时就有 ⇒ 就是它（与快照写法在
   * 同步场景下等价）；冷启动深链 ⇒ 地点后到时钉住（缺陷 3 的修法）。
   *
   * 为什么不直接用上下文里的 `locationId`：`fetchOrderDetail` 只吃 `orderId`（与地点无关），
   * 而反查 `findOrderIdByNo` 是**按地点**查的 —— 页面上这张订单属于**旧**地点，顶栏一切换就拿
   * **新**地点去重查，必然查不到（服务端 404/空）⇒ 一张正常显示的订单被打成「订单不存在」。
   * 因此「地点首次可用 ⇒ 解析一次」与「顶栏切换 ⇒ 不重解析旧订单」被写成两个显式时机，
   * 不再依赖快照的副作用；切换本身也保持**零请求**（`routes.spec.tsx` 的冻结断言）。
   */
  const [pinnedLocationId, setPinnedLocationId] = useState<string | null>(locationId);

  // 地点后到（冷启动深链）时钉住；已经在手上时 `current ?? locationId` 是 no-op（不会多渲染）
  useEffect(() => {
    if (locationId === null) return;
    setPinnedLocationId((current) => current ?? locationId);
  }, [locationId]);

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyLineId, setBusyLineId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [delivering, setDelivering] = useState(false);
  /**
   * 这份订单数据的**接收时刻**（R-06 ④、§6.4 规则 7）。
   *
   * 取 `fetchOrderDetail` 回来的那一刻，**不是** `order.createdAt`：后者是「顾客什么时候下的单」，
   * 一张三天前下的单今天被重新拉取，`createdAt` 仍是三天前，而这份数据是刚刚收到的 ——
   * 拿它冒充新鲜度，会让「我看到的这份数据有多旧」这个问题永远得到一个错误答案。
   * 也不是 `stream.freshness`（那是**接单面板队列**的接收时刻，与这一张单的详情是两条取数路径）。
   */
  const [dataFreshness, setDataFreshness] = useState<DataFreshness | null>(null);

  /**
   * 这一笔收款是不是**离线扫码取单**的（契约 §12.2 的 `origin = "OFFLINE_SCAN"`）。
   *
   * 两个来源**都要**，缺一不可：
   *   · `browserOffline`：此刻断网 ⇒ 收款只能进离线队列；
   *   · `isOfflineScan(orderNo)`：这单是断网时扫出来的（本次会话记着）—— 网**恢复之后**
   *     再收款，它仍然是离线取单的收款，必须带 `origin` + `verifiedWithCustomer`，
   *     否则服务端判 422、而店员会看到一句莫名其妙的「缺少核对」。
   */
  const offlineScan = browserOffline || isOfflineScan(order?.orderNo);

  /**
   * 把一条离线操作写进队列（**唯一的写入口**，见 `offline/queue.ts`）。
   *
   * 返回 `false` = 没记下（本机没有 IndexedDB）。调用方据此**不做**乐观更新：
   * 「界面动了、队列里没有」是最坏的一种失败 —— 店员以为记下了，重启后什么都没有。
   */
  const enqueueOffline = useCallback(
    async (operation: SyncOperationInput): Promise<boolean> => {
      try {
        await enqueueOperation(queueStore, operation);
        return true;
      } catch {
        setError(OFFLINE_QUEUE_UNAVAILABLE_TEXT);
        return false;
      }
    },
    [queueStore],
  );

  const load = useCallback(async (): Promise<void> => {
    if (pinnedLocationId === null) {
      setError(ORDER_NO_LOCATION_TEXT);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const orderId = stateOrderId ?? (await findOrderIdByNo(pinnedLocationId, orderNo));
      if (orderId === null) {
        setOrder(null);
        setError('订单不存在');
        return;
      }
      const detail = await fetchOrderDetail(orderId);
      setOrder(detail);
      setNotes(detail.notes ?? '');
      // ★ R-06 ④：时间戳记在**数据到手**这一行，与 `detail.createdAt` 无关
      setDataFreshness(freshnessOf(new Date(), null));
    } catch (e) {
      setError(readServerMessage(e) ?? '加载订单失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [orderNo, pinnedLocationId, stateOrderId]);

  /**
   * 取数时机：**本页钉住地点的那一刻跑一次**，且只跑一次。
   *
   *   · 挂载时地点就在 ⇒ 与改之前完全一致（同步发起反查 + 取详情）；
   *   · 冷启动深链 ⇒ 先如实说「请先选择地点」、**一个请求都不发**（也不谎报「加载订单中…」），
   *     地点到的这一次渲染再跑 —— 缺陷 3 的修法；
   *   · 顶栏**切换**地点 ⇒ `pinnedLocationId` 不变 ⇒ 本条 effect 不重跑 ⇒ 零请求、页面不动。
   *
   * 「重新加载」按钮走的是同一个 `load`：它按**钉住的地点**重试（页面上这张订单就属于那个地点，
   * 拿切换后的新地点去重试只会再空一次）。
   */
  useEffect(() => {
    if (pinnedLocationId === null) {
      setError(ORDER_NO_LOCATION_TEXT);
      setLoading(false);
      return;
    }
    // 地点到了：「请先选择地点」此刻起就是一句假话，先收掉它（`load` 开头也会清一次错误，
    // 这里显式写出来是为了让「提示只在没地点时为真」成为本 effect 的性质，而不是取数的副作用）
    setError((current) => (current === ORDER_NO_LOCATION_TEXT ? null : current));
    void load();
  }, [load, pinnedLocationId]);

  /**
   * 收款徽标由 `order:paid-confirmed` 事件驱动（§3.3）：同一地点的**另一台**收银端确认收款后，
   * 本页的「✅ 已收款」必须自己亮起来 —— 不订阅的话它只能靠刷新。
   *
   * ⚠️ 载荷比订阅回调的基类 `OrderEventPayload` 多一个 `paidConfirmedAt`
   * （契约 §19.3 / A19/A24 的 `OrderPaidConfirmedEvent`）⇒ 这里显式收窄；缺字段时兜一个时间戳，
   * 因为把 `undefined` 当成「未确认」会让徽标永不亮起（判据是 `paidConfirmedAt !== null`）。
   *
   * **按 `orderId` 过滤**：别的订单的确认不得改本页 —— 跨订单污染正是「界面说已收款、
   * 实际没有」那类安静失败。
   */
  useEffect(() => {
    const orderId = order?.id;
    if (orderId === undefined) return undefined;
    return stream.onOrderEvent((event, payload) => {
      if (event !== 'order:paid-confirmed' || payload.orderId !== orderId) return;
      const stamped: Partial<OrderPaidConfirmedEvent> = payload;
      const paidConfirmedAt = stamped.paidConfirmedAt ?? new Date().toISOString();
      setOrder((current) =>
        current === null
          ? current
          : { ...current, paidConfirmedAt: payload.paidConfirmed ? paidConfirmedAt : null },
      );
    });
  }, [order?.id, stream]);

  const pick = useCallback(
    async (lineId: string, nextPickedQty: number): Promise<void> => {
      if (order === null) return;
      setBusyLineId(lineId);
      setError(null);
      setNotice(null);
      /*
       * 离线分支（§6.8「离线可用范围」：挑拣勾选可写、进离线队列）。
       *
       * ⚠️ 这里**不**改本页的 `done`：`done` 是**服务端**下发的唯一判据（§4.5「客户端不得重算」），
       * 而离线时没有人算得出它。硬在本机算一个「挑完了」，会让交付门在一个服务端并不认可的状态上
       * 放行 —— 那正是「界面说可以交付、服务端说缺挑拣」。因此离线只**如实说**「已记录、联网后上传」，
       * 进度条等补传成功后由服务端那一份答案推动。
       */
      if (browserOffline) {
        const queued = await enqueueOffline(
          offlineActions.pick(order.orderNo, [{ orderLineId: lineId, pickedQty: nextPickedQty }]),
        );
        if (queued) setNotice(OFFLINE_PICK_QUEUED_TEXT);
        setBusyLineId(null);
        return;
      }
      try {
        const result = await pickLines(order.id, [{ lineId, pickedQty: nextPickedQty }]);
        setOrder((current) => (current === null ? current : mergeLineResults(current, result)));
        navigator.vibrate?.(15);
      } catch (e) {
        setError(readServerMessage(e) ?? '勾选失败，请重试');
      } finally {
        setBusyLineId(null);
      }
    },
    [browserOffline, enqueueOffline, order],
  );

  const markShort = useCallback(
    async (lineId: string, quantity: number, pickedQty: number): Promise<void> => {
      if (order === null) return;
      setBusyLineId(lineId);
      setError(null);
      setNotice(null);
      try {
        // 缺货是**累计值**（§12.2）：把这一行剩下的全部标缺
        const result = await shortLines(order.id, [{ lineId, shortQty: quantity - pickedQty }]);
        setOrder((current) => (current === null ? current : mergeLineResults(current, result)));
        // 金额两侧都取服务端下发值；任一侧读不出来就只说「金额以服务端为准」，
        // **不编一个 `¥0.00`**（附录 B1s）
        const before = formatServerYuan(result.totalAmountBefore);
        const after = formatServerYuan(result.totalAmount);
        setNotice(
          before === null || after === null
            ? `已标记缺货：${AMOUNT_UNAVAILABLE_TEXT}（释放 ${result.releasedQuantity} 件）`
            : `已标记缺货：金额 ${before} → ${after}（释放 ${result.releasedQuantity} 件）`,
        );
      } catch (e) {
        setError(readServerMessage(e) ?? '标记缺货失败，请重试');
      } finally {
        setBusyLineId(null);
      }
    },
    [order],
  );

  const saveNotes = useCallback(async (): Promise<void> => {
    if (order === null) return;
    if (notes.length > 100) {
      setError('备注最多 100 个字符');
      return;
    }
    setSavingNotes(true);
    setError(null);
    try {
      const saved = await updateOrderNotes(order.id, notes);
      setOrder((current) => (current === null ? current : { ...current, notes: saved.notes }));
      setNotice('备注已保存');
    } catch (e) {
      setError(readServerMessage(e) ?? '保存备注失败，请重试');
    } finally {
      setSavingNotes(false);
    }
  }, [notes, order]);

  const total = useMemo(() => (order === null ? 0 : order.lines.length), [order]);
  const doneCount = useMemo(
    () => (order === null ? 0 : order.lines.filter((line) => line.done).length),
    [order],
  );

  /**
   * 确认收款（§6.4）：收款方式与核对勾选来自弹窗，响应里的 `paidConfirmedAt` 是徽标的唯一出处。
   *
   * 三条路：
   *   ① **离线**（`browserOffline`）⇒ 进离线队列（`CONFIRM_PAYMENT`），本机乐观点亮收款徽标
   *      —— §6.8 明写「离线期间所有写操作在本机立即生效」，而队列页会把「还没上传」如实列出来；
   *   ② **离线取单但已恢复联网** ⇒ 走在线端点，**带上 `origin` + `verifiedWithCustomer`**
   *      （契约 §12.2：不带就是 422 `CUSTOMER_VERIFICATION_REQUIRED`）；
   *   ③ 普通在线收款 ⇒ 请求体**逐字不变**（只有 `method`）—— 那是 13 套 e2e 的回归断言。
   *
   * ⚠️ **客户端的先拦**（§6.4 规则 5b）：离线取单且没勾核对 ⇒ 连请求都不构造。
   * 服务端那一道是 422，但断网时它根本到不了店员眼前 —— 先拦是为了让「该勾什么」当场可见。
   */
  const confirm = useCallback(
    async (method: PaymentMethod, verified: boolean): Promise<void> => {
      if (order === null) return;
      if (offlineScan && !verified) {
        setError(VERIFY_REQUIRED_TEXT);
        return;
      }
      setPaying(true);
      setError(null);
      setNotice(null);
      try {
        if (browserOffline) {
          const queued = await enqueueOffline(offlineActions.confirmPayment(order.orderNo, method, verified));
          if (queued) {
            setOrder((current) =>
              current === null ? current : { ...current, paidConfirmedAt: new Date().toISOString() },
            );
            setPaymentOpen(false);
            setNotice(OFFLINE_PAID_QUEUED_TEXT);
          }
          return;
        }
        const result = await confirmPayment(
          order.id,
          offlineScan
            ? { method, origin: 'OFFLINE_SCAN', verifiedWithCustomer: true }
            : { method },
        );
        setOrder((current) =>
          current === null ? current : { ...current, paidConfirmedAt: result.paidConfirmedAt },
        );
        setPaymentOpen(false);
        setNotice(`已确认收款（${paymentMethodLabel(method)}）`);
      } catch (e) {
        setError(readServerMessage(e) ?? '确认收款失败，请重试');
      } finally {
        setPaying(false);
      }
    },
    [browserOffline, enqueueOffline, offlineScan, order],
  );

  /**
   * 交付（§12.2）。
   *
   * ⚠️ 「能不能交付」的**权威判定在服务端**：按钮的 `disabled` 只是体验，真正拦下交付的是
   * 400 `ORDER_GATE_NOT_SATISFIED`。只看 HTTP 状态会把「缺收款」与「缺挑拣」混成一句话，
   * 因此这里按 `readErrorCode` 分辨，并按 `details.missing` **逐项**说出来。
   */
  const deliver = useCallback(async (): Promise<void> => {
    if (order === null) return;
    setDelivering(true);
    setError(null);
    setNotice(null);
    /*
     * 离线交付：进队列，**不跳转**。
     *
     * 「能不能交付」的权威判定在服务端（下面的 400 `ORDER_GATE_NOT_SATISFIED`），离线时无人可判
     * ⇒ 跳到接单面板就是在演一出「交付成功」的戏。留在本页 + 一句如实提示，
     * 补传的结果（`APPLIED` / `CONFLICT`）由 `/cashier/offline` 的条目状态说话。
     */
    if (browserOffline) {
      const queued = await enqueueOffline(offlineActions.complete(order.orderNo));
      if (queued) setNotice(OFFLINE_COMPLETE_QUEUED_TEXT);
      setDelivering(false);
      return;
    }
    try {
      await completeOrder(order.id);
      navigate('/cashier', { replace: true });
    } catch (e) {
      if (readErrorCode(e) === 'ORDER_GATE_NOT_SATISFIED') {
        const missing = readServerDetails(e)?.missing;
        const items = Array.isArray(missing)
          ? missing.filter((item): item is string => typeof item === 'string')
          : [];
        const text = items
          .map((item) => (item === 'PAYMENT' ? '尚未确认收款' : item === 'PICKING' ? '还有商品未挑完' : item))
          .join('、');
        setError(`交付失败：${text === '' ? '交付条件未满足' : text}`);
      } else {
        setError(readServerMessage(e) ?? '交付失败，请重试');
      }
    } finally {
      setDelivering(false);
    }
  }, [browserOffline, enqueueOffline, navigate, order]);

  /**
   * 是否已收款 = `paidConfirmedAt !== null`（§4.5 的单一出处）。
   * **不是** `status === 'READY'`：收款与挑拣并行，只收款不会 READY，只挑完也不会。
   */
  const paid = order !== null && order.paidConfirmedAt !== null && order.paidConfirmedAt !== undefined;
  /** 是否全部挑完：只读服务端下发的逐行 `done`（§4.5），本文件不重算 */
  const allPicked = order !== null && order.lines.length > 0 && order.lines.every((line) => line.done);

  if (loading) {
    return (
      <div data-testid="cashier-order-page">
        <p className="py-8 text-center text-gray-500">加载订单中…</p>
      </div>
    );
  }

  if (order === null) {
    return (
      <div data-testid="cashier-order-page" className="flex flex-col items-center gap-3 py-12">
        <p data-testid="order-error" className="font-bold text-red-600">
          {error ?? '订单不存在'}
        </p>
        <Button variant="secondary" onClick={() => void load()}>
          重新加载
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="cashier-order-page" className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center gap-2 rounded-lg border-2 border-black p-3">
        <span className="text-base font-bold">订单 {orderNoShort(order.orderNo)}</span>
        <span className="text-base font-bold">短码 {order.shortCode}</span>
        <span
          data-testid="order-status-badge"
          className={`rounded border px-1.5 py-0.5 text-xs font-bold ${ORDER_STATUS_CLASS[order.status]}`}
        >
          {ORDER_STATUS_TEXT[order.status]}
        </span>
        <span data-testid="order-total" className="ml-auto text-xl font-bold">
          {formatServerYuan(order.totalAmount) ?? AMOUNT_UNAVAILABLE_TEXT}
        </span>
      </header>

      {error !== null && (
        <p data-testid="order-error" className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
          {error}
        </p>
      )}
      {notice !== null && (
        <p data-testid="order-notice" className="rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">
          {notice}
        </p>
      )}

      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">挑拣清单</h2>
          <span data-testid="pick-progress" className="text-sm font-bold">
            已挑拣 {doneCount}/{total}
          </span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={doneCount}
          aria-valuemin={0}
          aria-valuemax={total}
          className="h-2 w-full rounded-full bg-gray-200"
        >
          <div
            className="h-2 rounded-full bg-black"
            style={{ width: total === 0 ? '0%' : `${Math.round((doneCount / total) * 100)}%` }}
          />
        </div>

        {order.lines.map((line) => (
          <PickLineRow
            key={line.id}
            line={line}
            busy={busyLineId === line.id}
            onPick={(target, next) => void pick(target.id, next)}
            onPickAll={(target) => void pick(target.id, target.quantity - target.shortQty)}
            onShort={(target) => void markShort(target.id, target.quantity, target.pickedQty)}
          />
        ))}
      </section>

      {/*
       * 收款确认 + 交付（§6.4）。
       * 交付按钮的 `disabled` 是**体验**（读服务端下发的 `paidConfirmedAt` 与逐行 `done`），
       * 真正的门是服务端的 400 `ORDER_GATE_NOT_SATISFIED` —— 见上面的 `deliver`。
       */}
      <section className="flex flex-col gap-2 rounded-lg border-2 border-black p-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">收款确认{allPicked ? ' ✅' : ''}</h2>
          {paid && (
            <span
              data-testid="paid-badge"
              className="rounded border border-black px-1.5 py-0.5 text-xs font-bold"
            >
              ✅ 已收款
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500">
            {formatServerYuan(order.totalAmount) ?? AMOUNT_UNAVAILABLE_TEXT}
          </span>
          <Button
            data-testid="confirm-payment"
            onClick={() => setPaymentOpen(true)}
            disabled={paid}
            className="disabled:bg-gray-200 disabled:text-gray-400"
          >
            确认已收款
          </Button>
          <Button
            data-testid="deliver"
            size="lg"
            onClick={() => void deliver()}
            disabled={!paid || !allPicked || delivering}
            className="disabled:bg-gray-200 disabled:text-gray-400"
          >
            {delivering ? '交付中…' : '交付'}
          </Button>
        </div>
      </section>

      <section className="flex flex-wrap items-end gap-2">
        <Input
          label="订单备注（最多 100 个字符）"
          className="w-72"
          data-testid="note-input"
          maxLength={100}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <Button variant="secondary" onClick={() => void saveNotes()} disabled={savingNotes}>
          {savingNotes ? '保存中…' : '保存备注'}
        </Button>
      </section>

      {/* 金额取**服务端**的订单合计（§6.4 规则 1）：这里传的是 `order.totalAmount`，不是行累加 */}
      <PaymentConfirmModal
        open={paymentOpen}
        totalAmount={order.totalAmount}
        orderNo={order.orderNo}
        busy={paying}
        offlineScan={offlineScan}
        dataAsOf={formatDataAsOf(dataFreshness)}
        dataStale={isStale(dataFreshness, new Date())}
        onCancel={() => setPaymentOpen(false)}
        onConfirm={(method, verified) => void confirm(method, verified)}
      />
    </div>
  );
}
